import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Download, 
  Calendar,
  DollarSign,
  ShoppingCart,
  Users,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Filter
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dataHelpers } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PDVBreadcrumb from '../../components/pdv/PDVBreadcrumb';

const PDVReports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  const [reportData, setReportData] = useState({
    totalSales: 0,
    totalRevenue: 0,
    totalStudents: 0,
    averageTicket: 0,
    salesByHour: [],
    salesByPaymentMethod: [],
    topProducts: [],
    recentTransactions: []
  });

  const company_id = user?.company_id;

  useEffect(() => {
    if (company_id) {
      loadReportData();
    }
  }, [company_id, dateFilter, customStartDate, customEndDate]);

  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;

    switch (dateFilter) {
      case 'today':
        startDate = new Date(today);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(today);
        endDate = customEndDate ? new Date(customEndDate) : new Date(today);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        startDate = new Date(today);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
    }

    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();

      // Carregar dados de vendas
      const salesData = await dataHelpers.getSales(company_id, {
        startDate,
        endDate,
        limit: 1000
      });

      // Processar dados
      const totalSales = salesData.length;
      const totalRevenue = salesData.reduce((sum, sale) => sum + (parseFloat(sale.total_amount) || 0), 0);
      const uniqueStudents = new Set(salesData.map(sale => sale.student_id).filter(Boolean)).size;
      const averageTicket = totalSales > 0 ? totalRevenue / totalSales : 0;

      // Vendas por hora
      const salesByHour = {};
      salesData.forEach(sale => {
        const hour = new Date(sale.created_at).getHours();
        salesByHour[hour] = (salesByHour[hour] || 0) + 1;
      });

      const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
        hour: `${hour.toString().padStart(2, '0')}:00`,
        sales: salesByHour[hour] || 0
      }));

      // Vendas por método de pagamento
      const paymentMethods = {};
      salesData.forEach(sale => {
        const method = sale.payment_method || 'outros';
        paymentMethods[method] = (paymentMethods[method] || 0) + 1;
      });

      const paymentMethodData = Object.entries(paymentMethods).map(([method, count]) => ({
        method: translatePaymentMethod(method),
        count,
        percentage: ((count / totalSales) * 100).toFixed(1)
      }));

      // Produtos mais vendidos (simular)
      const topProducts = [
        { name: 'Lanche Natural', quantity: 15, revenue: 75.00 },
        { name: 'Refrigerante', quantity: 12, revenue: 36.00 },
        { name: 'Salgado', quantity: 8, revenue: 24.00 },
        { name: 'Suco Natural', quantity: 6, revenue: 30.00 },
        { name: 'Doce', quantity: 4, revenue: 12.00 }
      ];

      setReportData({
        totalSales,
        totalRevenue,
        totalStudents: uniqueStudents,
        averageTicket,
        salesByHour: hourlyData,
        salesByPaymentMethod: paymentMethodData,
        topProducts,
        recentTransactions: salesData.slice(0, 10)
      });

    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      toast.error('Erro ao carregar dados dos relatórios');
    } finally {
      setLoading(false);
    }
  };

  const translatePaymentMethod = (method) => {
    const translations = {
      'cash': 'Dinheiro',
      'credit': 'Cartão de Crédito',
      'debit': 'Cartão de Débito',
      'pix': 'PIX',
      'balance': 'Saldo do Aluno',
      'outros': 'Outros'
    };
    return translations[method] || method;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const exportData = () => {
    toast.success('Funcionalidade de exportação em desenvolvimento');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard/pdv/dashboard')}
                className="mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Relatórios do Dia</h1>
            </div>
            <Button onClick={exportData}>
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Breadcrumb */}
        <PDVBreadcrumb />
        
        {/* Filtros de Data */}
        <Card className="mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
              <Filter className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Período
                </label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="today">Hoje</option>
                  <option value="yesterday">Ontem</option>
                  <option value="week">Últimos 7 dias</option>
                  <option value="month">Este mês</option>
                  <option value="custom">Personalizado</option>
                </select>
              </div>
              
              {dateFilter === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Inicial
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Final
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando relatórios...</p>
          </div>
        ) : (
          <div className="space-y-6">
            
            {/* Métricas Principais */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100">
                      <ShoppingCart className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Total de Vendas</p>
                      <p className="text-2xl font-bold text-gray-900">{reportData.totalSales}</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Faturamento</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(reportData.totalRevenue)}</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Alunos Atendidos</p>
                      <p className="text-2xl font-bold text-gray-900">{reportData.totalStudents}</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-orange-100">
                      <TrendingUp className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Ticket Médio</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(reportData.averageTicket)}</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Vendas por Método de Pagamento */}
              <Card>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Métodos de Pagamento</h3>
                    <PieChart className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div className="space-y-4">
                    {reportData.salesByPaymentMethod.map((method, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            index === 0 ? 'bg-blue-500' :
                            index === 1 ? 'bg-green-500' :
                            index === 2 ? 'bg-purple-500' :
                            index === 3 ? 'bg-orange-500' : 'bg-gray-500'
                          }`}></div>
                          <span className="text-gray-700">{method.method}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-900">{method.count}</span>
                          <span className="text-sm text-gray-500 ml-2">({method.percentage}%)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Produtos Mais Vendidos */}
              <Card>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Produtos Mais Vendidos</h3>
                    <BarChart3 className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div className="space-y-4">
                    {reportData.topProducts.map((product, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-500">{product.quantity} unidades</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">{formatCurrency(product.revenue)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>

            {/* Vendas por Hora */}
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Vendas por Horário</h3>
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                </div>
                
                <div className="grid grid-cols-12 gap-2">
                  {reportData.salesByHour.map((data, index) => (
                    <div key={index} className="text-center">
                      <div 
                        className="bg-blue-200 rounded-t-md mb-1"
                        style={{ 
                          height: `${Math.max(data.sales * 10, 4)}px`,
                          minHeight: '4px'
                        }}
                      ></div>
                      <div className="text-xs text-gray-500 transform -rotate-45 origin-top">
                        {data.hour}
                      </div>
                      <div className="text-xs font-semibold text-gray-700 mt-1">
                        {data.sales}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Transações Recentes */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Transações Recentes</h3>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horário</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.recentTransactions.map((transaction, index) => (
                        <tr key={transaction.id || index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDateTime(transaction.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {transaction.students?.name || 'Consumidor Final'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {translatePaymentMethod(transaction.payment_method)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                            {formatCurrency(transaction.total_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDVReports;
